import { useResource } from "@/hooks/useResource";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState, ErrorState, EmptyState } from "@/components/common/States";
import { fmtCurrency, fmtNumber, fmtPercent, titleCase } from "@/lib/format";
import { apiErrorMessage } from "@/lib/api";
import { Calculator } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Cell, Legend,
} from "recharts";

const MONEY_RE = /cost|price|value|amount|margin|revenue|variance/i;
const PCT_RE = /pct|percent|%/i;

function fmtAuto(key, v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "number") {
    if (PCT_RE.test(key)) return fmtPercent(v);
    if (MONEY_RE.test(key)) return fmtCurrency(v);
    return fmtNumber(v);
  }
  return String(v);
}

function findKey(obj, re) {
  return Object.keys(obj).find((k) => re.test(k));
}

function AdaptiveTable({ rows }) {
  const cols = Object.keys(rows[0]).filter((k) => typeof rows[0][k] !== "object");
  return (
    <div className="rounded-lg border border-stone-200 bg-white shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-stone-50 border-b border-stone-200">
              {cols.map((c) => (
                <th key={c} className="whitespace-nowrap px-4 py-2.5 text-left text-[11px] font-bold uppercase tracking-wider text-stone-500">{titleCase(c)}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {rows.map((r, i) => (
              <tr key={i} data-testid={`costing-row-${i}`} className="hover:bg-amber-50/50">
                {cols.map((c) => (
                  <td key={c} className="whitespace-nowrap px-4 py-2.5 text-xs sm:text-sm font-mono tabular-nums text-stone-800">{fmtAuto(c, r[c])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CostCharts({ rows }) {
  const sample = rows[0];
  const nameKey = findKey(sample, /product.*name|^name$|recipe/i) || Object.keys(sample)[0];
  const currentKey = findKey(sample, /current.*cost|currentCost/i);
  const decidedKey = findKey(sample, /decided.*cost|decidedCost/i);
  const varKey = findKey(sample, /variance.*pct|variancePct/i);
  const marginKey = findKey(sample, /margin.*pct|marginPct|gross.*margin/i);

  const top = rows.slice(0, 12).map((r) => ({
    name: String(r[nameKey] ?? "").slice(0, 14),
    current: currentKey ? Number(r[currentKey]) || 0 : 0,
    decided: decidedKey ? Number(r[decidedKey]) || 0 : 0,
    variance: varKey ? Number(r[varKey]) || 0 : 0,
    margin: marginKey ? Number(r[marginKey]) || 0 : 0,
  }));

  const hasCosts = currentKey || decidedKey;
  const hasVar = varKey || marginKey;
  if (!hasCosts && !hasVar) return null;

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      {hasCosts && (
        <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
          <h3 className="font-display text-base font-semibold text-stone-900">Current vs Decided Cost</h3>
          <p className="mb-4 text-xs text-muted-foreground">Per product (top 12)</p>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={top} margin={{ top: 8, right: 8, left: -12, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" vertical={false} />
                <XAxis dataKey="name" angle={-35} textAnchor="end" height={60} tick={{ fontSize: 11, fill: "#78716C" }} />
                <YAxis tick={{ fontSize: 11, fill: "#78716C" }} axisLine={false} tickLine={false} />
                <RTooltip contentStyle={{ borderRadius: 8, border: "1px solid #E7E5E4", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="decided" name="Decided" fill="#0284C7" radius={[4, 4, 0, 0]} />
                <Bar dataKey="current" name="Current" fill="#B45309" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      {hasVar && (
        <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
          <h3 className="font-display text-base font-semibold text-stone-900">Cost Variance %</h3>
          <p className="mb-4 text-xs text-muted-foreground">Red bars exceed 5% tolerance</p>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={top} margin={{ top: 8, right: 8, left: -12, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" vertical={false} />
                <XAxis dataKey="name" angle={-35} textAnchor="end" height={60} tick={{ fontSize: 11, fill: "#78716C" }} />
                <YAxis tick={{ fontSize: 11, fill: "#78716C" }} axisLine={false} tickLine={false} />
                <RTooltip contentStyle={{ borderRadius: 8, border: "1px solid #E7E5E4", fontSize: 12 }} />
                <Bar dataKey={varKey ? "variance" : "margin"} name={varKey ? "Variance %" : "Margin %"} radius={[4, 4, 0, 0]}>
                  {top.map((d, i) => (
                    <Cell key={i} fill={Math.abs(varKey ? d.variance : d.margin) > 5 && varKey ? "#E11D48" : "#059669"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Costing() {
  const { data, isLoading, isError, error, refetch } = useResource("/costing-reports/summary");

  const rows = Array.isArray(data) ? data : data?.rows || data?.items || data?.products || [];
  const scalars = data && !Array.isArray(data)
    ? Object.entries(data).filter(([, v]) => typeof v === "number")
    : [];

  return (
    <div className="space-y-6">
      <PageHeader title="Costing & Variance" subtitle="Recipe version cost, decided cost, current cost, variance & margin — computed by the backend." />

      {isLoading ? (
        <LoadingState label="Loading costing summary…" />
      ) : isError ? (
        <ErrorState message={apiErrorMessage(error)} onRetry={refetch} />
      ) : (
        <>
          {scalars.length > 0 && (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {scalars.map(([k, v]) => (
                <div key={k} data-testid={`costing-kpi-${k}`} className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
                  <div className="text-xs uppercase tracking-wide text-stone-500">{titleCase(k)}</div>
                  <div className="mt-1 font-display text-xl font-bold text-stone-900">{fmtAuto(k, v)}</div>
                </div>
              ))}
            </div>
          )}
          {rows.length > 0 ? (
            <>
              <CostCharts rows={rows} />
              <AdaptiveTable rows={rows} />
            </>
          ) : scalars.length === 0 ? (
            <EmptyState
              icon={Calculator}
              title="No costing data yet"
              message="Costing insights appear once products have approved recipes and ingredient prices in the shared ERP."
            />
          ) : null}
        </>
      )}
    </div>
  );
}
